import { QWSService } from '../types/algorithm';

/**
 * Safely parses a numeric value from CSV, returning NaN for missing/invalid data
 * CRITICAL: Returns NaN (not 0) for missing data to avoid injecting fake zeros.
 * Missing data should be ignored in min/max computation and aggregation.
 */
function parseNumericValue(value: string): number {
  const x = parseFloat(value);
  return Number.isFinite(x) ? x : NaN;
}

/**
 * Parses QWS dataset from CSV text format
 * Expected format: ResponseTime, Availability, Throughput, Successability, 
 * Reliability, Compliance, BestPractices, Latency, Documentation, ServiceName, WSDL_URL
 * 
 * CRITICAL FIX: Missing/invalid numeric fields are now parsed as NaN (not 0).
 * This prevents fake zeros from corrupting min/max ranges, normalization, and energy/cost calculations.
 */
export function parseQWSDataset(csvText: string): QWSService[] {
  const lines = csvText.trim().split('\n');
  const services: QWSService[] = [];

  for (const line of lines) {
    if (!line.trim()) continue;

    const parts = line.split(',').map(p => p.trim());
    if (parts.length < 9) continue;

    try {
      const service: QWSService = {
        responseTime: parseNumericValue(parts[0]),
        availability: parseNumericValue(parts[1]),
        throughput: parseNumericValue(parts[2]),
        successability: parseNumericValue(parts[3]),
        reliability: parseNumericValue(parts[4]),
        compliance: parseNumericValue(parts[5]),
        bestPractices: parseNumericValue(parts[6]),
        latency: parseNumericValue(parts[7]),
        documentation: parseNumericValue(parts[8]),
        serviceName: parts[9] || '',
        wsdlUrl: parts[10] || '',
      };

      services.push(service);
    } catch (error) {
      console.warn('Failed to parse line:', line, error);
    }
  }

  return services;
}

/**
 * Simple seeded random number generator for deterministic task-specific variation
 * Uses a linear congruential generator (LCG) for reproducibility
 */
function seededRandom(seed: number): () => number {
  let state = seed;
  return () => {
    state = (state * 1103515245 + 12345) & 0x7fffffff;
    return state / 0x7fffffff;
  };
}

/**
 * Applies task-specific variation to a QoS value
 * Uses deterministic variation based on global seed + task/service/property indices for reproducibility
 * Variation range: ±5% to simulate real-world task-specific performance differences
 * 
 * CRITICAL: Preserves NaN values (missing data) - do not apply variation to NaN
 * 
 * @param baseValue The base QoS value from the dataset
 * @param task Task index
 * @param serviceIdx Service index
 * @param propertyIdx Property index
 * @param globalSeed Global seed for reproducibility (combined with indices for deterministic variation)
 */
function applyTaskVariation(
  baseValue: number,
  task: number,
  serviceIdx: number,
  propertyIdx: number,
  globalSeed: number
): number {
  // Preserve NaN (missing data) - do not apply variation
  if (!Number.isFinite(baseValue)) return baseValue;
  
  // Skip variation for zero values to avoid division issues
  if (baseValue === 0) return baseValue;
  
  // Create a deterministic seed from global seed + task, service, and property indices
  // This ensures reproducibility: same global seed + same indices = same variation
  const combinedSeed = ((globalSeed * 7919 + task * 1000000 + serviceIdx * 1000 + propertyIdx) % 2147483647);
  const random = seededRandom(combinedSeed);
  
  // Apply ±5% variation (deterministic but task-specific)
  const variation = (random() - 0.5) * 0.1; // Range: [-0.05, +0.05]
  const variedValue = baseValue * (1 + variation);
  
  // Ensure non-negative values (some properties like responseTime, latency must be >= 0)
  return Math.max(0, variedValue);
}

/**
 * Builds quality matrix from QWS services and task count
 * Creates a 3D array: matrix[task][service][property]
 * This avoids Map size limits for large datasets
 * 
 * CRITICAL FIX: Each task now gets task-specific QoS variations to prevent trivial solutions.
 * Without this, all tasks would have identical service options, making the optimal solution
 * trivial (just pick the same best service for all tasks). Task-specific variation simulates
 * real-world scenarios where the same service may perform differently for different tasks.
 * 
 * THESIS REPRODUCIBILITY: Uses a seeded RNG for deterministic task-specific variation.
 * The same seed will produce identical results across runs, ensuring reproducibility for
 * thesis experiments. The seed is logged for documentation purposes.
 * 
 * @param services Array of QWS services from the dataset
 * @param taskCount Number of tasks
 * @param seed Optional seed for RNG. If not provided, generates a deterministic seed based on current time.
 *             For thesis experiments, provide a fixed seed and log it for reproducibility.
 * @returns Object containing the quality matrix and the seed used
 */
export function buildQualityMatrix(
  services: QWSService[],
  taskCount: number,
  seed?: number
): { matrix: number[][][]; seed: number } {
  // Generate seed if not provided (for backward compatibility)
  // Use a deterministic seed based on current time (milliseconds since epoch)
  // For thesis experiments, provide a fixed seed explicitly
  const actualSeed = seed ?? Math.floor(Date.now() % 2147483647);
  
  // Log seed for reproducibility (important for thesis experiments)
  console.log(`[QWS Matrix Builder] Using seed: ${actualSeed} for deterministic task-specific variation`);
  
  // 3D array: [task][service][property]
  const matrix: number[][][] = [];

  for (let task = 0; task < taskCount; task++) {
    matrix[task] = [];
    for (let serviceIdx = 0; serviceIdx < services.length; serviceIdx++) {
      const service = services[serviceIdx];
      
      // Apply task-specific variation to each property using the global seed
      // This ensures different tasks have different QoS values, making the problem non-trivial
      // Same seed + same indices = same variation (reproducible)
      matrix[task][serviceIdx] = [
        applyTaskVariation(service.responseTime, task, serviceIdx, 0, actualSeed),
        applyTaskVariation(service.availability, task, serviceIdx, 1, actualSeed),
        applyTaskVariation(service.throughput, task, serviceIdx, 2, actualSeed),
        applyTaskVariation(service.successability, task, serviceIdx, 3, actualSeed),
        applyTaskVariation(service.reliability, task, serviceIdx, 4, actualSeed),
        applyTaskVariation(service.compliance, task, serviceIdx, 5, actualSeed),
        applyTaskVariation(service.bestPractices, task, serviceIdx, 6, actualSeed),
        applyTaskVariation(service.latency, task, serviceIdx, 7, actualSeed),
        applyTaskVariation(service.documentation, task, serviceIdx, 8, actualSeed),
      ];
    }
  }

  return { matrix, seed: actualSeed };
}

